import { isEqual } from 'lodash';

import { IAdresse } from 'components/AddressTypeahead/types';

import {
  AdresseViewModel,
  EnhetViewModel,
  LeverandorKundenummerDto,
} from 'services/KjederegisteretAdminApi/EnhetApi/types';
import { ChangeRequestCommand } from 'services/KjederegisteretAdminApi/ChangeRequestApi/types';

export const toAdresseFromViewModel = (advm: AdresseViewModel): IAdresse | undefined => {
  return advm
    ? {
        gateadresse: advm.gateadresse,
        postnr: advm.postnr,
        poststed: advm.poststed,
        formattedForOneLine: advm.formattedForOneLine,
        longitude: advm.longitude,
        latitude: advm.latitude,
        landkode: advm.landkode || 'NO',
        customOption: false,
      }
    : undefined;
};

export const getChangeRequestCommandInitialValue = (
  leverandorKundenummerHosTsKundeId: string,
): ChangeRequestCommand => ({
  gln: '',
  organisasjonsnr: '',
  isOrgnrValidated: false,
  lopenummer: '',
  juridisknavn: '',
  enhetnavn: '',
  telefon: '',
  epost: '',
  hjemmeside: '',
  dagligLeder: '',
  bransjeId: null,
  segment: null,
  notat: '',
  leverandorKundenummer: [],
  kontonummer: '',
  salgsareal: null,
  oppstartsDato: null,
  fakturaEpost: '',
  omsetning: null,
  markedskjedeNr: null,
  regionalkjedeNr: null,
  leverandorTsKundeId: leverandorKundenummerHosTsKundeId,
  nedlagtDato: null,
  isInactive: null,
  besoksadresse: {
    gateadresse: '',
    postnr: '',
    poststed: '',
    formattedForOneLine: '',
    longitude: null,
    latitude: null,
    adressetype: 'Besøksadresse',
  },
  copyPostAdresse: false,
});

export const getChangeRequestCommandUpdateValue = (
  enhet: EnhetViewModel,
  leverandorKundenummerHosTsKundeId: string,
  copyPostadresse: boolean,
  kundenummer?: LeverandorKundenummerDto[],
): ChangeRequestCommand => ({
  enhetId: enhet.id,
  lopenummer: enhet.lopenr,
  gln: enhet.firma.gln,
  organisasjonsnr: enhet.firma.organisasjonsnr,
  isOrgnrValidated: false,
  juridisknavn: enhet.firma.juridisknavn,
  enhetnavn: enhet.enhetnavn,
  telefon: enhet.firma.telefon,
  epost: enhet.firma.epost,
  hjemmeside: enhet.hjemmeside,
  dagligLeder: enhet.dagligLeder,
  bransjeId: enhet.bransje?.id,
  segment: enhet.segment,
  notat: enhet.notat,
  leverandorKundenummer: kundenummer?.map((o) => o.kundenummer) || [],
  kontonummer: enhet.firma.kontonr || '',
  leverandorTsKundeId: leverandorKundenummerHosTsKundeId,
  nedlagtDato: enhet.firma.nedlagtDato,
  salgsareal: enhet.firma.salgsareal,
  oppstartsDato: enhet.firma.oppstartsdato,
  fakturaEpost: enhet.firma.fakturaEpost,
  markedskjedeNr: enhet.salgsOgMarkedsforingsKjede?.number
    ? Number(enhet.salgsOgMarkedsforingsKjede.number)
    : null,
  regionalkjedeNr: enhet.regionalKjede?.number ? Number(enhet.regionalKjede.number) : null,
  omsetning: enhet.firma.omsetning,
  enhetsgrupperingsNr: null,

  isInactive: enhet.isInactive,
  besoksadresse: {
    gateadresse: enhet.besoksadresse?.gateadresse || '',
    postnr: enhet.besoksadresse?.postnr || '',
    poststed: enhet.besoksadresse?.poststed || '',
    formattedForOneLine: enhet.besoksadresse?.formattedForOneLine || '',
    longitude: enhet.besoksadresse?.longitude || undefined,
    latitude: enhet.besoksadresse?.latitude || undefined,
    adressetype: 'Besøksadresse',
  },
  copyPostAdresse: copyPostadresse,
  hovedGrossist: enhet.grossist
    ? {
        kundenummerHosGrossist: enhet.grossist.kundenummerHosGrossist,
        grossistEnhetNavn: enhet.grossist.grossistEnhetNavn,
        grossistGln: enhet.grossist.grossistGln,
      }
    : null,
  andreGrossister:
    enhet.grossister && enhet.grossister.length > 0
      ? enhet.grossister
          .filter((x) => !x.erHovedgrossist)
          .map((grossist) => ({
            kundenummerHosGrossist: grossist.kundenummerHosGrossist,
            grossistEnhetNavn: grossist.grossistEnhetNavn,
            grossistGln: grossist.grossistGln,
          }))
      : null,
});

export type CompareStatus = 'ny' | 'ignorer' | 'lik' | 'ulik' | 'ugyldig';

export const qaCompareSimpleValue = (a: string, b: string): CompareStatus => {
  const upperA = a?.toUpperCase();
  const upperB = b?.toUpperCase();

  if (upperA && !upperB) {
    return 'ny';
  }

  if (!upperA && upperB) {
    return 'ignorer';
  }

  if (!upperA && !upperB) {
    return 'ignorer';
  }

  if (upperA === upperB) {
    return 'lik';
  }

  return 'ulik';
};

export const qaContainsInText = (textToSearch: string, value: string): CompareStatus => {
  if (!textToSearch) {
    return 'ignorer';
  }

  if (textToSearch && !value) {
    return 'ny';
  }

  if (textToSearch.indexOf(value) > -1) {
    return 'lik';
  } else {
    return 'ulik';
  }
};

export const qaExistInCommaSeparatedString = (
  textToSearch: string,
  value: string,
): CompareStatus => {
  if (!textToSearch && !value) {
    return 'ignorer';
  }

  if (textToSearch && !value) {
    return 'ignorer';
  }

  if (!textToSearch && value) {
    return 'ny';
  }

  let separatedValues: string[] = textToSearch.split(',').map((item: string) => item.trim());
  if (separatedValues && value && separatedValues.some((x) => x === value)) {
    return 'lik';
  } else {
    return 'ulik';
  }
};

const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) {
    return true;
  }

  if (obj1 == null || typeof obj1 !== 'object' || obj2 == null || typeof obj2 !== 'object') {
    return false;
  }

  let keys1 = Object.keys(obj1);
  let keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (let key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};

/** sammenligner to objekter */
export const qaCompareObjectValues = (incoming: Object, existing: Object): CompareStatus => {
  // if all props in incoming has a value and all props in obj2 are null -> ny
  if (!Object.values(incoming).every((x) => !x) && Object.values(existing).every((y) => !y)) {
    return 'ny';
  }

  // if not all props in incoming has a value -> ignore
  if (Object.values(incoming).every((x) => !x) && !Object.values(existing).every((y) => !y)) {
    return 'ignorer';
  }

  // if all props are equal -> lik
  if (deepEqual(incoming, existing)) {
    return 'lik';
  }

  return 'ulik';
};
